import React, { useCallback } from 'react';
import { useDispatch } from 'umi';
import { Flex, Form, Image, Typography } from 'antd';

import ThumbnailUpload from '@/components/ThumbnailUpload';
import { F } from '@/utils/intl';
import { CheckAccess } from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import { getApiPrefix } from '@/constants/path';
import { URLParamBuilder } from '@/utils/functions';
import { getEffectiveApiRoot } from '@/services/api/request';
import { deleteBranchServiceThumbnailRequest, deleteBranchThumbnailRequest } from '@/services/api/location';
import { thumbnailTarget } from './thumbnailTarget';
import styles from './LocationThumbnailField.module.less';

type LocationThumbnailFieldProps = {
  /** Branch id; also the branch refetched afterwards (services come nested in it). */
  branchId: string;
  /** Set for a branch service's thumbnail; omit for the branch's own. */
  serviceId?: string;
  thumbnail?: string | null;
  /** A service type's image the service shows while it has none of its own. */
  inheritedThumbnail?: string | null;
};

/** Optional image widgets show for a branch or a service; uploads go to Laravel. */
const LocationThumbnailField: React.FC<LocationThumbnailFieldProps> = ({ branchId, serviceId, thumbnail, inheritedThumbnail }) => {
  const dispatch = useDispatch();
  const target = thumbnailTarget(branchId, serviceId);

  const refresh = useCallback(
    () => dispatch({ type: 'location/getBranch', payload: { branchId } }),
    [dispatch, branchId],
  );

  // ThumbnailUpload's catch is what reports a failed delete; request() itself never rejects.
  const handleDelete = useCallback(async () => {
    const { response } = (serviceId
      ? await deleteBranchServiceThumbnailRequest({ params: { serviceId } })
      : await deleteBranchThumbnailRequest({ params: { branchId } })) as { response?: Response };
    if (!response?.ok) throw new Error(`Thumbnail delete failed (${response?.status})`);
  }, [branchId, serviceId]);

  return (
    <CheckAccess access={[target.permission, PERMISSIONS.BUSINESS_PARTNER]}>
      <Form.Item
        className={styles.thumbnailField}
        label={<F id="common.thumbnail" defaultMessage="Thumbnail" />}
        tooltip={serviceId ? (
          <F
            id="component.LocationThumbnail.serviceTooltip"
            defaultMessage="Optional. Booking widgets can show it as this service's picture. Without one, the service type's image is used."
          />
        ) : (
          <F
            id="component.LocationThumbnail.branchTooltip"
            defaultMessage="Optional. Booking widgets can show it as this location's picture."
          />
        )}
        colon={false}
      >
        <Flex align="center" gap={12}>
          <ThumbnailUpload
            key={serviceId || branchId}
            currentThumbnail={thumbnail || undefined}
            uploadUrl={URLParamBuilder(target.path, {
              params: target.params,
              root: `${getEffectiveApiRoot()}${getApiPrefix(target.path, 'POST')}`,
            }, false)}
            accept="image/png,image/jpeg"
            showPreviewButton
            onDelete={handleDelete}
            onUploadSuccess={refresh}
            onDeleteSuccess={refresh}
          />
          {!thumbnail && inheritedThumbnail && (
            <Flex align="center" gap={8} className={styles.inherited}>
              <Image src={inheritedThumbnail} width={24} height={24} preview={false} />
              <Typography.Text type="secondary">
                <F
                  id="component.LocationThumbnail.inherited"
                  defaultMessage="Using the service type's image. Upload one to replace it here."
                />
              </Typography.Text>
            </Flex>
          )}
        </Flex>
      </Form.Item>
    </CheckAccess>
  );
};

export default React.memo(LocationThumbnailField);
